import React from "react";
import {
  Search,
  ArrowUpDown,
  Clock,
  Users,
  Inbox,
  RefreshCw
} from "lucide-react";

import { MenuButton } from "../../../components/Shared/Buttons/MenuButton";
import { AccessHeaderLogo } from "../../../components/Shared/Buttons/AccessHeaderLogo";
import { HeaderContainer } from "../../../components/Layout/HeaderContainer/HeaderContainer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../../components/eds/EDSDropdownMenu";
import { EDSButton } from "../../../components/eds/EDSButton";
import { EnquiriesSortOption, FacetCounts } from "../types";

const SORT_OPTIONS: { value: EnquiriesSortOption; label: string }[] = [
  { value: "date_desc", label: "Date (newest)" },
  { value: "date_asc", label: "Date (oldest)" },
  { value: "customer_asc", label: "Customer (A-Z)" },
  { value: "customer_desc", label: "Customer (Z-A)" },
  { value: "party_size_desc", label: "Party size (largest)" },
  { value: "party_size_asc", label: "Party size (smallest)" }
];

export interface EnquiriesHeaderProps {
  searchQuery: string;
  sortOption: EnquiriesSortOption;
  facetCounts: FacetCounts | null;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSortChange: (value: EnquiriesSortOption) => void;
  onRefresh: () => void;
}

const EnquiriesHeaderContent: React.FC<EnquiriesHeaderProps> = ({
  searchQuery,
  sortOption,
  facetCounts,
  onSearchChange,
  onSortChange,
  onRefresh
}) => {
  const currentSortLabel =
    SORT_OPTIONS.find((opt) => opt.value === sortOption)?.label ?? "Sort";

  return (
    <div className="flex-shrink-0 p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            size={16}
          />
          <input
            type="text"
            placeholder="Search enquiries..."
            value={searchQuery}
            onChange={onSearchChange}
            aria-label="Search enquiries"
            className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background hover:bg-accent transition-colors"
                aria-label="Sort enquiries"
              >
                <ArrowUpDown size={16} />
                <span className="text-sm">{currentSortLabel}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {SORT_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onSortChange(option.value)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <EDSButton
            variant="secondary"
            onClick={onRefresh}
            aria-label="Refresh enquiries"
          >
            <RefreshCw size={16} />
          </EDSButton>
        </div>

        {facetCounts && (
          <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-background border border-border shadow-sm">
            <span className="flex items-center gap-1 text-blue-500">
              <Inbox size={16} />
              <span className="text-sm font-medium">
                {facetCounts.status.new}
              </span>
            </span>
            <span className="flex items-center gap-1 text-amber-500">
              <Clock size={16} />
              <span className="text-sm font-medium">
                {facetCounts.status.in_progress}
              </span>
            </span>
            <span className="flex items-center gap-1 text-green-500">
              <Users size={16} />
              <span className="text-sm font-medium">
                {facetCounts.status.complete}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const EnquiriesHeader: React.FC<EnquiriesHeaderProps> = (props) => {
  return (
    <HeaderContainer
      start={
        <>
          <MenuButton />
          <AccessHeaderLogo />
        </>
      }
      middle={<EnquiriesHeaderContent {...props} />}
      featureSentimentName="Enquiries"
    />
  );
};
